import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import ColumnSearch from "../Common/ColumnSearch";
import { useCampaignByCompanyId } from "@/hooks/queries/campaigns";
import Loader from "@/components/Loader";
import { Menu, Transition } from "@headlessui/react";
import { Fragment } from "react";
import { Eye, LucideScreenShare } from "lucide-react";
import {
  CalendarIcon,
  DocumentIcon,
  PencilIcon,
  EllipsisVerticalIcon,
} from "@heroicons/react/24/outline";
import "react-toastify/dist/ReactToastify.css";
import {
  MoreVertical,
  Edit,
  Briefcase,
  PlusCircle,
  Search,
  ChevronDown,
  Plus,
  Calendar,
  Clock,
  Trash2,
  Copy,
  Download,
  X,
  Users,
  KeyRound as Pound,
  CheckCircle,
  XCircle,
  FileText,
} from "lucide-react";
import { campaignsService } from "@/services/campaignsService";
import CampaignTable from "../campaign/CampaignTable"; // Import the new CampaignTable component
import Pagination from "../Common/Pagination";
import useToastStore from "@/components/Common/Toast/toastStore";
import { useDebounce } from "@/utils/helpers";
import { PER_PAGE } from "@/config";
import usePaginationStore from "@/hooks/stores/usePaginationStore";

const Campaigns = ({ setActiveTab, setCampaignId }) => {
  const { id: companyId } = useParams();
  const [filteredCampaigns, setFilteredCampaigns] = useState([]);
  const [columnFilters, setColumnFilters] = useState({
    title: "",
    status: "",
    start_date: "",
    end_date: "",
  });
  const [activeMenu, setActiveMenu] = useState(null);
  const navigate = useNavigate();
  const { getPerPage, setPerPage } = usePaginationStore();
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(24);

  const [serverPagination, setServerPagination] = useState({
    current_page: 1,
    per_page: getPerPage("campaigns-table"),
    total: 0,
    last_page: 1,
    next_page_url: null,
    prev_page_url: null,
  });

  const menuRef = useRef(null);
  // Use the query hook to fetch campaigns
  const {
    data: { campaigns, pagination } = { campaigns: [], pagination: {} },
    isLoading,
    isError,
    refetch: refetchCampaigns,
  } = useCampaignByCompanyId(companyId, {
    per_page: serverPagination.per_page,
    page: serverPagination.current_page,
    search: searchTerm,
  });

  const { showToast } = useToastStore;
  useEffect(() => {
    if (pagination) {
      setServerPagination(pagination);
    }
    if (campaigns) {
      setFilteredCampaigns(campaigns);
    }
  }, [campaigns]);

  useEffect(() => {
    const filtered = campaigns.filter((campaign) => {
      return Object.entries(columnFilters).every(([key, value]) => {
        if (!value) return true;
        return String(campaign[key])
          .toLowerCase()
          .includes(value.toLowerCase());
      });
    });
    setFilteredCampaigns(filtered);
  }, [columnFilters, campaigns, serverPagination, searchTerm]);

  // const handleColumnSearch = (column, value) => {
  //   setSearchTerm(term);
  //   setPagination((prev) => ({ ...prev, current_page: 1 }));
  // };

  const handleEdit = (id) => {
    navigate(`/campaigns/${id}`); // Navigate to the edit page for the selected campaign
  };

  const handleDelete = (id) => {
    const updatedCampaigns = campaigns.filter((campaign) => campaign.id !== id);
    setFilteredCampaigns(updatedCampaigns);
    localStorage.setItem("campaigns", JSON.stringify(updatedCampaigns)); // Update local storage
    alert("Campaign deleted successfully!");
  };

  const handleView = (id) => {
    navigate(`/campaigns/${id}/view`); // Navigate to view the campaign
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  const indexOfLastItem =
    serverPagination?.current_page * serverPagination?.per_page || 0;
  const indexOfFirstItem = indexOfLastItem - serverPagination?.per_page || 0;
  const currentCampaigns = filteredCampaigns.slice(
    indexOfFirstItem,
    indexOfLastItem,
  );
  const totalPages = Math.ceil(
    filteredCampaigns.length / (serverPagination?.per_page || 1),
  );

  const handleVacanciesClick = (campaign) => {
    setActiveTab("vacancies");
    setCampaignId(campaign.id);
  };

  const handleStatusChange = async (campaignId, newStatus) => {
    try {
      // Call the API to update the status
      await campaignsService.updateCampaignStatus(campaignId, {
        status: newStatus, // Send the new status value in the payload
      });

      // Update local state
      setFilteredCampaigns((prevCampaigns) =>
        prevCampaigns.map((campaign) =>
          campaign.id === campaignId
            ? { ...campaign, status: newStatus } // Update the status
            : campaign,
        ),
      );

      // Show success message
      showToast({
        message: `Campaign ${newStatus === 2 ? "archived" : newStatus === 0 ? "marked as Draft" : "published"} successfully`,
        type: "success",
      });

      // Close the menu
      setActiveMenu(null);
    } catch (error) {
      console.error("Error updating campaign status:", error);
      showToast({
        message: error.message || "Failed to update campaign status",
        type: "error",
      });
    }
  };

  const [isRefetching, setIsRefetching] = useState(false);
  useEffect(() => {
    const fetchData = async () => {
      setIsRefetching(true);
      await refetchCampaigns();
      setIsRefetching(false);
    };
    fetchData();
  }, [serverPagination.current_page, serverPagination.per_page, searchTerm]);
  const handlePageChange = (page) => {
    // Simply update the current page, fetchData will be triggered by the useEffect
    setServerPagination({ ...serverPagination, current_page: page });
    // console.log(serverPagination);
  };

  // Handle pagination dropdown change
  const handlePaginationChange = (selectedOption) => {
    const newPerPage = selectedOption.value;
    setPerPage("campaigns-table", newPerPage);
    setServerPagination((prev) => ({
      ...prev,
      per_page: newPerPage,
      current_page: 1, // Reset to first page when changing items per page
    }));
  };

  // function to handle search term input
  const updateSearchTerm = (term) => {
    setSearchTerm(term);
    setServerPagination({ ...serverPagination, current_page: 1 });
  };

  // defined useDebounce for search term input
  const debouncedSearch = useDebounce(updateSearchTerm, 1000);

  const [tempSearchTerm, setTempSearchTerm] = useState("");
  const handleSearchChange = (term) => {
    setTempSearchTerm(term);
    debouncedSearch(term);
  };

  // Add this useEffect for click outside handling
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setActiveMenu(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  if (isError) {
    return <div>Error loading campaigns</div>;
  }

  return (
    <div className="w-full">
      <div className="flex justify-between items-end mb-6 gap-4 flex-wrap">
        <div className="flex flex-col gap-1">
          <h2 className="text-xl font-semibold text-foreground">Campaigns</h2>
          <p className="text-muted-foreground text-sm font-normal">
            Manage and track your recruitment campaigns.
          </p>
        </div>

        <div className="flex items-center gap-4 flex-wrap flex-1 justify-end">
          <div className="w-full max-w-sm flex flex-col gap-1.5">
            <div className="relative group">
              <div className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground group-focus-within:text-muted-foreground transition-colors">
                <Search className="w-5 h-5" />
              </div>
              <input
                type="text"
                placeholder="Search"
                value={tempSearchTerm}
                onChange={(e) => handleSearchChange(e.target.value)}
                className="w-full pl-10 pr-12 py-2 bg-card rounded-lg border border-input shadow-sm focus:ring-2 focus:ring-secondary/20 focus:border-secondary outline-none transition-all text-foreground placeholder:text-muted-foreground text-sm"
              />
            </div>
          </div>

          <button
            onClick={() => navigate(`/companies/${companyId}/add-campaign`)}
            className="h-10 px-4 py-2 bg-primary-dark hover:bg-primary-dark text-white rounded-lg shadow-sm flex justify-center items-center gap-2 transition-all group active:scale-95"
          >
            <Plus className="w-4 h-4" />
            <span className="text-sm font-semibold">Add Campaign</span>
          </button>
        </div>
      </div>

      {isRefetching || isLoading ? (
        <Loader />
      ) : (
        <CampaignTable
          campaigns={filteredCampaigns.filter(
            (campaign) => campaign.status == 1 || campaign.status == 0,
          )}
          handleEdit={handleEdit}
          handleDelete={handleDelete}
          handleStatusChange={handleStatusChange}
          setActiveTab={setActiveTab}
          setCampaignId={setCampaignId}
          setActiveMenu={setActiveMenu}
          activeMenu={activeMenu}
          onVacanciesClick={handleVacanciesClick}
          handleSearchChange={handleSearchChange}
          searchTerm={tempSearchTerm}
          serverPagination={serverPagination}
          onPageChange={handlePageChange}
          onPerPageChange={handlePaginationChange}
        />
      )}

      {/* <div className="flex justify-between items-center mt-6">
        <div className="text-sm text-muted-foreground">
          Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, filteredCampaigns.length)} of {filteredCampaigns.length} campaigns
        </div>
        <div className="flex space-x-2">
          <button
            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
            disabled={currentPage === 1}
            className={`px-4 py-2 border rounded-md text-sm font-medium
              ${currentPage === 1
                ? 'bg-muted text-muted-foreground cursor-not-allowed'
                : 'bg-card text-foreground hover:bg-muted'}`}
          >
            Previous
          </button>
          <button
            onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
            disabled={currentPage === totalPages}
            className={`px-4 py-2 border rounded-md text-sm font-medium
              ${currentPage === totalPages
                ? 'bg-muted text-muted-foreground cursor-not-allowed'
                : 'bg-card text-foreground hover:bg-muted'}`}
          >
            Next
          </button>
        </div>
      </div> */}
    </div>
  );
};

export default Campaigns;
